import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { createStore, StoreApi } from 'zustand/vanilla';

export interface ModalComponent<Props = void, Res = void> {
  (props: { props: Props; open: boolean; onClose: (props: Res) => void }): JSX.Element;
}

type ModalDef = {
  component: ModalComponent<unknown, unknown>;
  paths?: string[];
};
type ModalsDef = Record<string, ModalDef>;

type ModalParams<T extends ModalDef> = T extends { component: ModalComponent<infer R, unknown> }
  ? R
  : never;
type ModalReturn<T extends ModalDef> = T extends { component: ModalComponent<unknown, infer R> }
  ? R
  : never;

interface MountedModal {
  id: string;
  key: string;
  component: ModalComponent<unknown, unknown>;
  props: unknown;
  open: boolean;
  onClose: (args: unknown) => void;
}

interface ModalsOptions<T extends ModalsDef> {
  modals: T;
  unmountTimeout?: number;
}

interface ModalsState<T extends ModalsDef> {
  modals: T;
  mounted: MountedModal[];
}

export interface ModalManger<T extends ModalsDef> {
  store: StoreApi<ModalsState<T>>;
  open: <Key extends keyof T>(
    modalKey: Key,
    ...args: ModalParams<T[Key]> extends void ? [] : [ModalParams<T[Key]>]
  ) => Promise<ModalReturn<T[Key]>>;
}

export function createModalsManager<T extends ModalsDef>(
  optionsFactory: () => ModalsOptions<T>,
): ModalManger<T> {
  let options!: ModalsOptions<T>;
  const store: StoreApi<ModalsState<T>> = createStore<ModalsState<T>>(() => ({
    modals: {} as T,
    mounted: [],
  }));

  const close = async (modalId: string) => {
    store.setState((s) => ({
      mounted: s.mounted.map((modal) => (modal.id === modalId ? { ...modal, open: false } : modal)),
    }));

    await new Promise((resolve) => setTimeout(resolve, options.unmountTimeout ?? 500));

    store.setState((s) => ({ mounted: s.mounted.filter((modal) => modal.id !== modalId) }));
  };

  const open = async <Key extends keyof T>(
    modalKey: Key,
    ...args: ModalParams<T[Key]> extends void ? [] : [ModalParams<T[Key]>]
  ): Promise<ModalReturn<T[Key]>> => {
    const modelId = `${modalKey.toString()}-${crypto.randomUUID()}`;
    const params = args[0] as ModalParams<T[Key]>;

    return new Promise((resolve) => {
      const mount: MountedModal = {
        id: modelId,
        key: modalKey.toString(),
        open: true,
        component: store.getState().modals[modalKey].component,
        onClose: (args: ModalReturn<T[Key]>) => {
          void close(modelId)
            .then(() => resolve(args))
            .catch((err) => {
              throw new Error(`${err}`);
            });
        },
        props: params,
      };

      store.setState((s) => ({ mounted: [...s.mounted, mount] }));
    });
  };

  queueMicrotask(() => {
    options = optionsFactory();
    store.setState({ modals: options.modals });
  });

  return { store, open };
}

export function useModalsStore<T extends ModalsDef, R>(
  store: StoreApi<ModalsState<T>>,
  selector: (state: ModalsState<T>) => R,
): R {
  return useStore(store, selector);
}

const ModalContainer = <T extends ModalsDef>({
  store,
  modalId,
}: {
  store: StoreApi<ModalsState<T>>;
  modalId: string;
}) => {
  const modal = useModalsStore(store, (state) => state.mounted.find((m) => m.id === modalId));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(!!modal && modal.open);
  }, [modal]);

  if (!modal) return null;
  return <modal.component onClose={modal.onClose} props={modal.props} open={open} />;
};

export const ModalsContainer = <T extends ModalsDef>({
  store,
}: {
  store: StoreApi<ModalsState<T>>;
}) => {
  const mounted = useModalsStore(store, (state) => state.mounted);

  return (
    <>
      {mounted.map((modal) => (
        <ModalContainer store={store} modalId={modal.id} key={`${modal.id}`} />
      ))}
    </>
  );
};
